import { Component, Inject, Input, OnChanges, OnInit, Optional, SimpleChanges } from '@angular/core';

import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { EdiDestinationListItem, EdiDestinationService } from '@app/services/edi-destination.service';
import { EdiExportProcService, EdiExportProcTableNameItem } from '@app/services/edi-export-proc.service';
import { EdiTypeListItem, EdiTypeService } from '@app/services/edi-type.service';
import { P2ExportProcGet, P2ExportProcParams, P2ExportProcService, P2ExportProcWebpageItem } from '@app/services/p2-export-proc.service';
import { P2ImportProcListItem, P2ImportProcService } from '@app/services/p2-import-proc.service';
import { TailorAccessListItem, TailorAccessService } from '@app/services/tailor-access.service';

@Component({
    selector: 'tt-p2-export-proc',
    templateUrl: './p2-export-proc.component.html',
    styleUrls: ['./p2-export-proc.component.css'],
    standalone: false,
})
export class P2ExportProcComponent implements OnInit, OnChanges {
    @Input() public params?: P2ExportProcParams;

    public page?: P2ExportProcGet;

    public importProcedures: P2ImportProcListItem[] = [];

    public ediDestinations: EdiDestinationListItem[] = [];

    public ediTypes: EdiTypeListItem[] = [];

    public tableNames: EdiExportProcTableNameItem[] = [];

    public tailorAccesses: TailorAccessListItem[] = [];

    public webpages: P2ExportProcWebpageItem[] = [];

    public saving = false;

    constructor(
        private exportProc: P2ExportProcService,
        private importProc: P2ImportProcService,
        private ediDestination: EdiDestinationService,
        private ediType: EdiTypeService,
        private ediExportProc: EdiExportProcService,
        private tailorAccess: TailorAccessService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async save(): Promise<void> {
        if (!this.page) return;

        this.saving = true;
        try {
            await this.exportProc.saveExportProc(this.page);
            await this.navigator?.back();
        } finally {
            this.saving = false;
        }
    }

    private async init(): Promise<void> {
        const keyno = this.params?.p2_exportproc_keyno;
        if (keyno === undefined || keyno === null || `${keyno}` === '') return;

        await Promise.all([
            this.loadPage({ p2_exportproc_keyno: keyno }),
            this.loadImportProcedures(),
            this.loadEdiDestinations(),
            this.loadEdiTypes(),
            this.loadTableNames(),
            this.loadTailorAccesses(),
            this.loadWebpages(),
        ]);
    }

    private async loadPage(params: P2ExportProcParams): Promise<void> {
        this.page = await this.exportProc.getExportProc(params);
        this.navigator?.setHeading?.(this.page.p2_exportproc_name);
    }

    private async loadImportProcedures(): Promise<void> {
        this.importProcedures = await this.importProc.getImportProceduresList({});
    }

    private async loadEdiDestinations(): Promise<void> {
        this.ediDestinations = await this.ediDestination.getEdiDestinationsList({});
    }

    private async loadEdiTypes(): Promise<void> {
        this.ediTypes = await this.ediType.getEdiTypesList({});
    }

    private async loadTableNames(): Promise<void> {
        this.tableNames = await this.ediExportProc.getTableNamesList({});
    }

    private async loadTailorAccesses(): Promise<void> {
        this.tailorAccesses = await this.tailorAccess.getTailorAccessesList({});
    }

    private async loadWebpages(): Promise<void> {
        this.webpages = await this.exportProc.getWebpagesList({});
    }

    public ngOnInit(): void {
        if (this.navigator) {
            this.params = this.navigator.params as unknown as P2ExportProcParams;
            void this.init();
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']?.currentValue) void this.init();
    }
}
